import React, { ComponentType, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Autocomplete, Box, Button, Container, InputAdornment, Tab, Tabs, TextField } from '@mui/material';

import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import { GHAPriceCheckContainer } from '~/components/GHAPriceCheck/GHAPriceCheckContainer';
import GHAPriceCompetitivenessContainer from '~/components/GHAPriceCompetitiveness/GHAPriceCompetitivenessContainer';
import HotelGroupView from '~/components/GoogleAdsHotelBidding/HotelGroupView';
import SimpleVoucherForm from '~/components/Vouchers/SimpleVoucherForm';

import CampaignLinksPage from '~/pages/CampaignLinks/CampaignLinksPage';
import AudiencesPage from '~/pages/Marketing/AudiencesPage';
import OfferBypassRegionFilterPage from '~/pages/Marketing/OfferBypassRegionFilterPage';
import RegionRulePage from '~/pages/Marketing/RegionRulePage';
import VoucherAutoAssignRuleListPage from '~/pages/Vouchers/VoucherAutoAssignRuleListPage';
import VoucherFormPage from '~/pages/Vouchers/VoucherFormPage';

import { ROLE_PROMO_ADMIN_USER } from '~/consts/roles';
import tenants from '~/consts/tenant';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import ChannelMarkupContainer from '../ChannelMarkup/ChannelMarkup';
import ChannelMarkupV2ListPage from '../ChannelMarkup/ChannelMarkupV2ListPage';
import PageHeader from '../Common/Elements/PageHeader';
import ContentfulFormsContainer from '../ContentfulForms/ContentfulFormsContainer';
import ForcedRankingsConfigContainer from '../ForcedRankingsConfig/ForcedRankingsConfig';
import GHADiscountSummaryContainer from '../GHADiscountSummary/GHADiscountSummaryContainer';
import GHAPropertyManagementContainer from '../GHAPropertyManagement/GHAPropertyManagementContainer';
import BlackhawkGiftCardLogsContainer from '../GiftCards/GiftCardLogs/BlackhawkGiftCardLogsContainer';
import GiftCardLogsContainer from '../GiftCards/GiftCardLogs/GiftCardLogsContainer';
import GiftCardsContainer from '../GiftCards/GiftCards/GiftCardsContainer';
import AdCreativeAIAssistantContainer from '../GoogleAds/AdCreativeAIAssistantContainer';
import BidModificationHistory from '../GoogleAds/BidModificationHistory';
import CustomerListView from '../GoogleAds/CustomerListView';
import GoogleAdsCampaignsConfigContainer from '../GoogleAdsCampaignsConfig/GoogleAdsCampaignsConfig';
import GoogleAdsRuleTemplatesContainer from '../GoogleAdsRuleTemplates/GoogleAdsRuleTemplatesContainer';
import HintCoverageMatrixContainer from '../HintCoverageMatrix/HintCoverageMatrixContainer';
import AgentVouchers from '../LeAgentHub/components/AgentVouchers/AgentVouchers';
import AgentCommissions from '../LeAgentHub/components/Commission/AgentCommissions';
import QuotedCommissions from '../LeAgentHub/components/Commission/QuotedCommissions';
import AgentQuotes from '../LeAgentHub/components/Quotes/AgentQuotes';
import PinnedOffersContainer from '../PinnedOffers/PinnedOffers';
import PinnedOffersConfigContainer from '../PinnedOffersConfig/PinnedOffersConfig';
import InAppMessageContainer from '../Promo/InAppMessageContainer';
import PromoRequestContainer from '../Promo/PromoRequests/PromoRequestContainer';
import PromoRoleManagement from '../Promo/PromoRoles/PromoRoleManagement';
import PromoSearchContainer from '../Promo/PromoSearchContainer';
import PromoTesterContainer from '../Promo/PromoTester/PromoTesterContainer';
import ReferralLogs from '../Promo/ReferralLogs';
import SimplePromoForm from '../Promo/SimplePromoForm';
import OfferMetricsContainer from '../PromoDisplayConfig/OfferMetricsContainer';
import PromoDisplayConfigContainer from '../PromoDisplayConfig/PromoDisplayConfig';
import LoyaltyPromoKeyOptionsContainer from '../PromoV3/LoyaltyPromoKeyOptionsContainer';
import PromoContainerV3 from '../PromoV3/PromoContainer';
import ReferralEarnOptionsContainer from '../ReferralEarnOptions/ReferralEarnOptionsContainer';
import Tags from '../Tags/Tags';
import MediaViewPage from '../Videos/MediaListPage';
import VoucherDisplayConfigContainer from '../VoucherDisplayConfig/VoucherDisplayConfig';
import VoucherUsersSearchPage from '../Vouchers/VoucherUsersSearchPage';
import VouchersContainer from '../Vouchers/Vouchers';

import BadgesContainer from './Badges/BadgesContainer';
import CompedPackagesContainer from './CompedPackages/CompedPackagesContainer';
import ContentSearchPage from './Content/ContentSearchPage';
import FacebookContentImagesContainer from './FacebookContentImages/FacebookContentImagesContainer';
import FacebookImageTemplatesContainer from './FacebookImageTemplates/FacebookImageTemplatesContainer';
import LeLiveContainer from './LeLive/LeLiveContainer';
import OfferDeepLinksContainer from './OfferDeepLinks/OfferDeepLinksContainer';
import CampaignTags from './Tags';
import TemplatesContainer from './Templates/TemplatesContainer';

const CREATE_PROMO_TAB = 'create-promo';
const ADVANCED_PROMO_CREATE = 'advanced-create-promo';
const SEARCH_PROMO_TAB = 'search-promo';
const COMPED_TAB = 'comped';
const OFFER_DEEP_LINKS_TAB = 'offer-deep-links';

type TabConfig = {
  label: string;
  component: ComponentType<unknown>;
  tenants?: Set<string>;
  requiredRoles?: Array<string>;
};

function formatRequiredRoles(requiredRoles: Array<string>): string {
  return requiredRoles.join(', ');
}

export const tabsMap: Record<string, TabConfig> = {
  'agent-commissions': {
    label: 'Agent Commissions',
    component: AgentCommissions,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  'quoted-commissions': {
    label: 'Quoted Commissions',
    component: QuotedCommissions,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  'agent-vouchers': {
    label: 'Create Agent Voucher',
    component: AgentVouchers,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  'agent-quotes': {
    label: 'Agent Quotes',
    component: AgentQuotes,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  [SEARCH_PROMO_TAB]: {
    label: 'Search Promo Codes',
    component: PromoSearchContainer,
  },
  [CREATE_PROMO_TAB]: {
    label: 'Simple Promo Create',
    component: SimplePromoForm,
  },
  [ADVANCED_PROMO_CREATE]: {
    label: 'Advanced Promo Create',
    component: PromoContainerV3,
  },
  'promo-requests': {
    label: 'Promo Requests',
    component: PromoRequestContainer,
  },
  'promo-tester': {
    label: 'Promo Tester',
    component: PromoTesterContainer,
  },
  'promo-roles': {
    label: 'Promo Roles',
    component: PromoRoleManagement,
    requiredRoles: [ROLE_PROMO_ADMIN_USER],
  },
  'loyalty-promo-keys': {
    label: 'Promo Loyalty Reward Options',
    component: LoyaltyPromoKeyOptionsContainer,
  },
  'promo-display-config': {
    label: 'Promo Display Config',
    component: PromoDisplayConfigContainer,
  },
  'offer-metrics': {
    label: 'Offer Metrics',
    component: OfferMetricsContainer,
  },
  'in-app-messaging': {
    label: 'In App Promo Messaging',
    component: InAppMessageContainer,
  },
  [COMPED_TAB]: {
    label: 'Famils and competition winners',
    component: CompedPackagesContainer,
  },
  'campaign-tags': {
    label: 'Campaign Tags',
    component: CampaignTags,
  },
  'campaign-links': {
    label: 'Campaign Links',
    component: CampaignLinksPage,
  },
  tags: {
    label: 'Tags',
    component: Tags,
  },
  content: {
    label: 'Content',
    component: ContentSearchPage,
  },
  badges: {
    label: 'Badges',
    component: BadgesContainer,
  },
  templates: {
    label: 'Templates',
    component: TemplatesContainer,
  },
  [OFFER_DEEP_LINKS_TAB]: {
    label: 'Offer Deep Links',
    component: OfferDeepLinksContainer,
  },
  'gift-cards': {
    label: 'Gift Cards',
    component: GiftCardsContainer,
  },
  'gift-card-logs': {
    label: 'Gift Card Logs',
    component: GiftCardLogsContainer,
  },
  'blackhawk-gift-card-logs': {
    label: 'Blackhawk Logs',
    component: BlackhawkGiftCardLogsContainer,
  },
  'channel-markup': {
    label: 'Channel Markup',
    component: ChannelMarkupContainer,
  },
  'channel-markup-v2': {
    label: 'Channel Markup v2',
    component: ChannelMarkupV2ListPage,
  },
  'contentful-forms': {
    label: 'Forms',
    component: ContentfulFormsContainer,
  },
  'google-hotel-ads-property-management': {
    label: 'GHA Property Management',
    component: GHAPropertyManagementContainer,
  },
  'gha-property-discount-summary': {
    label: 'GHA Property Discount Summary',
    component: GHADiscountSummaryContainer,
  },
  'google-hotel-ads-price-check': {
    label: 'GHA Price Comparison',
    component: GHAPriceCheckContainer,
  },
  'google-hotel-ads-price-competitiveness': {
    label: 'GHA Price Competitiveness',
    component: GHAPriceCompetitivenessContainer,
  },
  'hotel-bidding': {
    label: 'Hotel Bidding',
    component: HotelGroupView,
  },
  'bid-modification-history': {
    label: 'Bid Modification History',
    component: BidModificationHistory,
  },
  'google-ads-creative-ai': {
    label: 'Google Ads Creative AI',
    component: AdCreativeAIAssistantContainer,
  },
  'referral-earn-options': {
    label: 'Referral Earn Options',
    component: ReferralEarnOptionsContainer,
  },
  referralLogs: {
    label: 'Referral Logs',
    component: ReferralLogs,
  },
  'google-ads-automation': {
    label: 'Google Ads Automation',
    component: CustomerListView,
  },
  'google-ads-campaigns-config': {
    label: 'Google Ads Campaigns Config',
    component: GoogleAdsCampaignsConfigContainer,
  },
  'google-ads-rule-templates': {
    label: 'Google Ads Rule Templates',
    component: GoogleAdsRuleTemplatesContainer,
  },
  'pull-with-hints-coverage': {
    label: 'Pull With Hints Coverage',
    component: HintCoverageMatrixContainer,
  },
  'forced-rankings-config': {
    label: 'Forced Rankings Config',
    component: ForcedRankingsConfigContainer,
  },
  'pinned-offers-config': {
    label: 'Pinned Offers Config',
    component: PinnedOffersConfigContainer,
  },
  'pinned-offers': {
    label: 'Pinned Offers',
    component: PinnedOffersContainer,
  },
  vouchers: {
    label: 'Vouchers',
    component: VouchersContainer,
  },
  'vouchers-create': {
    label: 'Create Voucher',
    component: VoucherFormPage,
  },
  'vouchers-simple-create': {
    label: 'Simple Voucher Create',
    component: SimpleVoucherForm,
  },
  'voucher-users': {
    label: 'Voucher Lookup',
    component: VoucherUsersSearchPage,
  },
  'voucher-display-config': {
    label: 'Voucher Display Config',
    component: VoucherDisplayConfigContainer,
  },
  'voucher-auto-assign-rules': {
    label: 'Voucher auto-assign rules',
    component: VoucherAutoAssignRuleListPage,
  },
  'video-gen': {
    label: 'Video Generator',
    component: MediaViewPage,
  },
  'facebook-image-templates': {
    label: 'Facebook Image Templates',
    component: FacebookImageTemplatesContainer,
  },
  'facebook-content-images': {
    label: 'Facebook Content Images',
    component: FacebookContentImagesContainer,
  },
  'le-live': {
    label: 'LE Live',
    component: LeLiveContainer,
  },
  'offer-bypass-region-filter': {
    label: 'Offer Region Bypass Filter',
    component: OfferBypassRegionFilterPage,
  },
  'region-rule': {
    label: 'Region rules',
    component: RegionRulePage,
  },
  audiences: {
    label: 'Audiences',
    component: AudiencesPage,
  },
} as const;

preRegisterSpotlightRoutes(expandTabRoutes('/marketing', 'Marketing', tabsMap));

export default function MarketingPage() {
  const { user } = useCurrentUser();
  const { tab } = useParams<'tab'>();
  const { push } = useAppNavigation();
  const [showAllTabs, setShowAllTabs] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState<string | null>(null);
  const key = tab || SEARCH_PROMO_TAB;

  const currentTab = tabsMap[key];

  const brand = useSelector((state: App.State) => state.tenant);
  const userRoles = user?.roles ?? [];
  const hasRole = useMemo(() => {
    return (requiredRoles: Array<string>) => requiredRoles.some((role) => userRoles.includes(role));
  }, [userRoles]);
  const rolesLoading = false;

  const tabsList = useMemo(() => {
    return Object.keys(tabsMap).filter((key) => {
      const tab = tabsMap[key];

      if (tab.requiredRoles !== undefined) {
        return hasRole(tab.requiredRoles);
      }

      // For tenant-based filtering, always show
      if (!tab.tenants) {
        return true;
      }
      if (tab.tenants.has(brand.value)) {
        return true;
      }
      return false;
    });
  }, [brand, hasRole]);
  const visibleTabsList = showAllTabs ? Object.keys(tabsMap) : tabsList;

  const title = `Marketing / ${currentTab.label}`;

  const isCurrentTabAccessible = useMemo(() => {
    if (currentTab.requiredRoles !== undefined) {
      return hasRole(currentTab.requiredRoles);
    }

    return true;
  }, [currentTab, hasRole]);
  const handleTabSelection = (_event: React.SyntheticEvent, value: string | null) => {
    if (!value) {
      return;
    }

    const selectedTab = tabsMap[value];
    if (selectedTab.requiredRoles !== undefined && !hasRole(selectedTab.requiredRoles)) {
      setPermissionMessage(
        `You need one of these roles to access this tab: ${formatRequiredRoles(selectedTab.requiredRoles)}`,
      );
      return;
    }

    setPermissionMessage(null);
    setShowAllTabs(false);
    push(`/marketing/${value}`);
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title="Marketing" />
        <Autocomplete
          options={visibleTabsList}
          value={key}
          onChange={handleTabSelection}
          getOptionLabel={(option) => tabsMap[option].label}
          renderOption={(props, option) => {
            const tabConfig = tabsMap[option];
            const isRestricted = tabConfig.requiredRoles !== undefined && !hasRole(tabConfig.requiredRoles);
            return (
              <Box
                component="li"
                {...props}
                sx={{
                  color: isRestricted ? 'error.main' : 'inherit',
                }}
              >
                {tabConfig.label}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tab"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setShowAllTabs(true);
                          setPermissionMessage(null);
                        }}
                        sx={{ minWidth: 0, px: 0, textTransform: 'none' }}
                      >
                        Show all tabs
                      </Button>
                    </InputAdornment>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs variant="scrollable" scrollButtons allowScrollButtonsMobile value={key}>
            {visibleTabsList.map((tab) => {
              const tabConfig = tabsMap[tab];
              const isRestricted = tabConfig.requiredRoles !== undefined && !hasRole(tabConfig.requiredRoles);
              return (
                <Tab
                  key={tab}
                  value={tab}
                  label={tabConfig.label}
                  to={`/marketing/${tab}`}
                  component={Link}
                  onClick={(event) => {
                    if (!isRestricted) {
                      setPermissionMessage(null);
                      setShowAllTabs(false);
                      return;
                    }
                    event.preventDefault();
                    setPermissionMessage(
                      `You need one of these roles to access this tab: ${formatRequiredRoles(tabConfig.requiredRoles ?? [])}`,
                    );
                  }}
                  sx={{
                    color: isRestricted ? 'error.main' : undefined,
                  }}
                />
              );
            })}
          </Tabs>
        </Box>
      </Container>

      {permissionMessage ? (
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Alert severity="error">{permissionMessage}</Alert>
        </Container>
      ) : null}

      {!rolesLoading &&
        !permissionMessage &&
        !isCurrentTabAccessible &&
        currentTab.requiredRoles?.includes(ROLE_PROMO_ADMIN_USER) && (
          <Container maxWidth="xl" sx={{ mt: 2 }}>
            <Alert severity="error">You need the {ROLE_PROMO_ADMIN_USER} role to access this tab.</Alert>
          </Container>
        )}

      {!rolesLoading &&
        !permissionMessage &&
        !isCurrentTabAccessible &&
        currentTab.requiredRoles !== undefined &&
        !currentTab.requiredRoles.includes(ROLE_PROMO_ADMIN_USER) && (
          <Container maxWidth="xl" sx={{ mt: 2 }}>
            <Alert severity="error">You don't have the role to access this tab.</Alert>
          </Container>
        )}

      {isCurrentTabAccessible && <Box component={currentTab.component} />}
    </>
  );
}
